<script>
import {defineComponent, ref, onMounted, onUnmounted, watch} from 'vue'
import {useCartStore} from "@js/store/CartStore.js";
import {getAddressSuggestions} from "@js/yandex-api.ts";
import {checkDocument} from "@js/helpers.ts";
import {Link} from "@inertiajs/vue3";
import IconArrowRight from "@js/components/Icons/IconArrowRight.vue";

export default defineComponent({
    name: "CartDelivery",
    components: {IconArrowRight, Link},

    setup() {
        const cartStore = useCartStore();

        const query = ref(cartStore.form.delivery_address);
        const suggestions = ref([]);
        const dropdownRef = ref(null);
        let debounce = null;

        const fetchSuggestions = () => {
            clearTimeout(debounce);

            debounce = setTimeout(async () => {
                if ((query.value || '').length < 3) {
                    suggestions.value = [];
                    return;
                }

                suggestions.value = await getAddressSuggestions(query.value);
            }, 300);
        };

        const selectSuggestion = (value) => {
            query.value = value;
            cartStore.form.delivery_address = value;
            suggestions.value = [];
        };

        watch(query, (val) => {
            cartStore.form.delivery_address = val;
        });

        const handleClickOutside = (event) => {
            if (!dropdownRef.value?.contains(event.target)) {
                suggestions.value = [];
            }
        };

        onMounted(() => {
            if (checkDocument()) {
                document.addEventListener('click', handleClickOutside);
            }

            cartStore.form.vehicle_type = null;
            cartStore.form.vehicle = null;
        });

        onUnmounted(() => {
            if (checkDocument()) {
                document.removeEventListener('click', handleClickOutside);
            }
        });

        return {
            cartStore,
            query,
            suggestions,
            fetchSuggestions,
            selectSuggestion,
            dropdownRef,
        };
    }
})
</script>

<template>
    <div class="bg-grayBg p-5 md:p-8 rounded-2xl space-y-6">
        <div>
            <h2 class="text-xl font-medium mb-3">Выберите способ доставки</h2>

            <div class="space-y-4">
                <label
                    class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                    :class="cartStore.form.delivery_type === 'pickup'
                        ? 'border-baseColor border-solid'
                        : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'"
                >
                    <input
                        type="radio"
                        v-model="cartStore.form.delivery_type"
                        value="pickup"
                        class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />

                    <div>
                        <div class="font-medium">Самовывоз</div>
                        <div class="text-sm text-gray-500">
                            Получение со склада или пункта выдачи. Условия уточнит менеджер.
                        </div>
                    </div>
                </label>

                <label
                    class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                    :class="cartStore.form.delivery_type === 'inside_mkad'
                        ? 'border-baseColor border-solid'
                        : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'"
                >
                    <input
                        type="radio"
                        v-model="cartStore.form.delivery_type"
                        value="inside_mkad"
                        class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />

                    <div>
                        <div class="font-medium">Доставка в пределах МКАД</div>
                        <div class="text-sm text-gray-500">
                            Стоимость и срок доставки менеджер рассчитает после оформления заявки.
                        </div>
                    </div>
                </label>

                <label
                    class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                    :class="cartStore.form.delivery_type === 'outside_mkad'
                        ? 'border-baseColor border-solid'
                        : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'"
                >
                    <input
                        type="radio"
                        v-model="cartStore.form.delivery_type"
                        value="outside_mkad"
                        class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />

                    <div>
                        <div class="font-medium">Доставка за пределами МКАД</div>
                        <div class="text-sm text-gray-500">
                            Рассчитаем доставку по адресу, объёму заказа и типу разгрузки.
                        </div>
                    </div>
                </label>

                <label
                    class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                    :class="cartStore.form.delivery_type === 'russia'
                        ? 'border-baseColor border-solid'
                        : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'"
                >
                    <input
                        type="radio"
                        v-model="cartStore.form.delivery_type"
                        value="russia"
                        class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />

                    <div>
                        <div class="font-medium">Доставка по России</div>
                        <div class="text-sm text-gray-500">
                            Подберём способ доставки после обработки заявки.
                        </div>
                    </div>
                </label>
            </div>
        </div>

        <div
            v-if="cartStore.form.delivery_type !== 'pickup'"
            class="mt-3"
        >
            <h2 class="text-xl font-medium">Укажите адрес доставки</h2>

            <div ref="dropdownRef" class="relative mb-4">
                <input
                    v-model="query"
                    @input="fetchSuggestions"
                    type="text"
                    placeholder="Адрес доставки"
                    autocomplete="off"
                    class="w-full mt-3 px-4 py-2 mb-1 border border-gray-300 focus:border-baseColor focus:ring-baseColor rounded-md"
                />

                <ul
                    v-if="suggestions.length"
                    class="absolute z-10 w-full bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto"
                >
                    <li
                        v-for="(suggestion, i) in suggestions"
                        :key="i"
                        @click="selectSuggestion(suggestion.label)"
                        class="px-4 py-2 md:hover:bg-gray-100 cursor-pointer text-sm"
                    >
                        {{ suggestion.label }}
                    </li>
                </ul>
            </div>
        </div>

        <div class="rounded-xl border border-orange-200 bg-orange-50 p-4">
            <div class="font-medium text-gray-900 mb-1">
                Доставка рассчитывается индивидуально
            </div>

            <p class="text-sm text-gray-600 leading-relaxed">
                Итоговая стоимость зависит от адреса, состава заказа, машины и условий разгрузки.
                После отправки заявки менеджер проверит заказ и согласует удобный вариант доставки.
            </p>

            <Link
                :href="route('deliveryAndShipping')"
                class="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold underline md:hover:text-navHoverColor transition"
            >
                Подробнее об условиях доставки <IconArrowRight class="w-3 h-3"/>
            </Link>
        </div>

        <div>
            <h2 class="text-xl font-medium mb-3">Выберите способ оплаты</h2>

            <div class="space-y-3">
                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input
                        type="radio"
                        v-model="cartStore.form.payment_type"
                        value="cash"
                        class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <span>Для физ. лиц без счёта</span>
                </label>

                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input
                        type="radio"
                        v-model="cartStore.form.payment_type"
                        value="card"
                        class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <span>Банковской картой</span>
                </label>

                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input
                        type="radio"
                        v-model="cartStore.form.payment_type"
                        value="invoice"
                        class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"
                    />
                    <span>Счёт для юр. лиц</span>
                </label>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
input[type="radio"],
input[type="checkbox"] {
    accent-color: #ef5b27;
}
</style>
